// Brand helper — Task #5 (Verticalization — Unified SuperAdmin).
//
// ONE place that answers "what brand am I, and what are its name / logo / links
// / company info?" Every page that currently hardcodes a brand value will read
// it from here instead (that replacement work is #13 SuperAdmin+Support,
// #17 momsite — NOT part of #5).
//
// SOURCE OF TRUTH:
//   #5  -> mock data in infrastructure/brand.config.js, selected by env var.
//   #26 -> this helper calls the real GET /vertical/config (Aminul's #11) at
//          startup and replaces the mock. The public API below stays the same,
//          so callers don't change again.
//
// Active-vertical selection reuses the existing VUE_APP_SITE env switch already
// used by daoSource/services/config.js. Unknown / missing -> DEFAULT_VERTICAL.

import { BRANDS, DEFAULT_VERTICAL } from '../infrastructure/brand.config'

// Picks the active vertical from VUE_APP_VERTICAL.
// NOTE: do NOT reuse VUE_APP_SITE here — that var already means "which app"
// (momsite vs SuperAdmin) in daoSource/services/config.js, which picks the API
// handler. Overloading it breaks auth when set to 'sn'/'ssh'. So branding uses
// its own key, VUE_APP_VERTICAL = 'circleram' | 'sn' | 'ssh'. Unknown/unset -> circleram.
// (This is the temporary #5 mock source; #26 replaces it with web-address + /vertical/config.)
// Maps any vertical code — the full one the API returns (schoolnode / sikhscholarhub, D44) or the short
// one (sn / ssh) — to the mock BRANDS key, so the right vertical's default logos are found when the API
// sends no logo path. Without this an API code of "schoolnode" misses BRANDS["sn"] and falls back to
// CircleRAM's logo for every vertical.
function normalizeBrandCode(code) {
  const raw = String(code || '').toLowerCase()
  if (raw === 'sn' || raw === 'schoolnode') return 'sn'
  if (raw === 'ssh' || raw === 'sikhscholarhub') return 'ssh'
  // 'circleram', '' -> CircleRAM
  return 'circleram'
}

function resolveVerticalCode() {
  return normalizeBrandCode(process.env.VUE_APP_VERTICAL)
}

class BrandService {
  constructor() {
    this._code = resolveVerticalCode()
    this._brand = BRANDS[this._code] || BRANDS[DEFAULT_VERTICAL]
  }

  // Full brand object for the active vertical.
  get current() {
    return this._brand
  }

  get code() {
    return this._brand.code
  }

  get name() {
    return this._brand.name
  }

  get legalName() {
    return this._brand.legalName
  }

  get productName() {
    return this._brand.productName
  }

  get superadminTitle() {
    return this._brand.superadminTitle
  }

  // key: 'main' | 'white' | 'small' | 'favicon'
  // Returns the raw logo value: a brand-relative path (mock) OR a full URL (#26, from the API).
  logo(key = 'main') {
    return this._brand.logo[key] || this._brand.logo.main
  }

  // The <img :src> value for a logo, correct whichever form logo(key) is in.
  // #26: the API returns a full URL (e.g. https://cdn.sn.ai/logo.png) — use it as-is. The mock returns a
  // brand-relative path (assets/images/..) — prefix Vue's own BASE_URL (its publicPath), so it resolves
  // against whatever origin, port AND sub-path the app is actually served from (root in dev, maybe
  // /superadmin/ in production). Not a hardcoded VUE_APP_BASE_URL, which pins a port that can be wrong.
  logoSrc(key = 'main') {
    const path = this.logo(key)
    if (!path) return ''
    if (/^(https?:)?\/\//i.test(path)) return path // full URL (or protocol-relative) -> as-is
    const base = (process.env.BASE_URL || '/').replace(/\/+$/, '')
    return base + '/' + String(path).replace(/^\/+/, '')
  }

  // key: any key under links{} in brand.config.js
  // (site, home, pricing, talkToSales, terms, privacy, refund, aboutUs,
  //  learn, blog, verticalDance/Corporate/Learning/Fitness)
  link(key) {
    return this._brand.links[key] || ''
  }

  // key: 'x' | 'linkedin' | 'facebook' | 'youtube' | 'instagram'
  social(key) {
    return this._brand.social[key] || ''
  }

  get companyAddress() {
    return this._brand.company.address
  }

  get supportEmail() {
    return this._brand.company.supportEmail
  }

  get salesEmail() {
    return this._brand.company.salesEmail || ''
  }

  get tenantBaseDomain() {
    return this._brand.tenantBaseDomain || ''
  }

  // Lets tests / the staging ?vertical= selector override the active brand.
  // In #26 the startup /vertical/config fetch will call this.
  setBrand(codeOrObject) {
    if (typeof codeOrObject === 'string') {
      this._brand = BRANDS[codeOrObject] || this._brand
      this._code = this._brand.code
    } else if (codeOrObject && codeOrObject.code) {
      this._brand = codeOrObject
      this._code = codeOrObject.code
    }
    // Keep the browser tab in sync: the favicon <link> and the page title are in index.html's
    // <head>, so switching vertical would otherwise leave the previous vertical's icon and title.
    this.applyDocumentHead()
    return this._brand
  }

  // Updates the browser tab's favicon and title to the active brand. The favicon <link> and <title>
  // live in index.html, outside Vue, so they must be set on the DOM directly after each brand change.
  applyDocumentHead() {
    if (typeof document === 'undefined') return
    const favicon = this.logoSrc('favicon')
    if (favicon) {
      let link = document.querySelector('link[rel="icon"], link[rel="shortcut icon"]')
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = favicon
    }
    if (this._brand && this._brand.superadminTitle) {
      document.title = this._brand.superadminTitle
    }
  }

  // #26: swap the active brand to the one the Vertical Settings API returned for the current
  // vertical (Aminul's GET /verticals/current, #11/#15). The API carries the values that change
  // per vertical today — name, logo/white logo/favicon, colour, support email and the
  // terms/privacy/pricing links; everything the API does not carry yet (social handles, the other
  // links, legal/product names, company address) falls back to this vertical's mock in
  // brand.config.js, so no consumer ever reads an empty value. Unknown code or a null payload
  // leaves the current brand untouched.
  applyFromApi(apiVertical) {
    if (!apiVertical || !apiVertical.Code) return this._brand

    // Find this vertical's mock (its default logos) by the normalized key; the API's full code
    // (schoolnode) would otherwise miss BRANDS (keyed sn/ssh) and fall back to CircleRAM's logos.
    const base = BRANDS[normalizeBrandCode(apiVertical.Code)] || BRANDS[DEFAULT_VERTICAL]
    // Prefer the API value only when it is actually filled; otherwise keep the mock's.
    const pick = (apiValue, fallback) =>
      (apiValue === undefined || apiValue === null || apiValue === '') ? fallback : apiValue

    const merged = {
      // Keep the real vertical code the API sent (schoolnode / sikhscholarhub); the normalized key above
      // was only used to find the default logos.
      code: String(apiVertical.Code).toLowerCase(),
      name: pick(apiVertical.Name, base.name),
      legalName: base.legalName,
      productName: base.productName,
      superadminTitle: pick(apiVertical.Name ? apiVertical.Name + ' | Superadmin' : '', base.superadminTitle),
      logo: {
        main: pick(apiVertical.LogoUrl, base.logo.main),
        white: pick(apiVertical.LogoWhiteUrl, base.logo.white),
        small: pick(apiVertical.LogoUrl, base.logo.small),
        favicon: pick(apiVertical.FaviconUrl, base.logo.favicon)
      },
      links: Object.assign({}, base.links, {
        terms: pick(apiVertical.TermsUrl, base.links.terms),
        privacy: pick(apiVertical.PrivacyUrl, base.links.privacy),
        pricing: pick(apiVertical.PricingUrl, base.links.pricing)
      }),
      social: Object.assign({}, base.social),
      company: Object.assign({}, base.company, {
        address: pick(apiVertical.BusinessAddress, base.company.address),
        supportEmail: pick(apiVertical.SupportEmail, base.company.supportEmail)
      }),
      primaryColor: pick(apiVertical.PrimaryColor, base.primaryColor || ''),
      tenantBaseDomain: pick(apiVertical.BaseDomain, base.tenantBaseDomain)
    }

    return this.setBrand(merged)
  }

  // Theme colour for the active vertical (from the API in #26; empty when the mock has none).
  get primaryColor() {
    return this._brand.primaryColor || ''
  }
}

// Single shared instance — import and use `brand.name`, `brand.logo('small')`, etc.
const brand = new BrandService()
export default brand
